import React, { useState, useEffect } from 'react';
import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, useLoaderData, useRouteError, isRouteErrorResponse } from "@remix-run/react";
import { ArrowTopRightOnSquareIcon, LinkIcon } from '@heroicons/react/24/solid';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';

import {getRelatedRepos} from '../server/ann-search.server';
import UpdatedAt from '../components/updatedAt';
import Description from '../components/description';
import {ExternalLinksList} from '../components/external_links_list';
import {RemixRepoListItem} from '../components/remix-repo-list-item';
import {RepoResult} from '../components/repo-list-item';
import {showThankYouToast, getModalElement} from '../components/utils';
import {apiCacheControlHeader} from '../server/utils.server';
import ButtonGroup from '../components/button-group';
import {HistoryType, addToHistory} from "../client/local-history.client";

export const meta: MetaFunction = ({ data, params }: { data: any, params: any }) => {
  // Average repo name length: ~ 14.5 characters
  // SELECT avg(length(repo_name)) FROM gh_meta WHERE repo_name IS NOT NULL;

  // Title tag: 50-60 characters optimal, max 70 characters
  // Meta description: 140-160 characters optimal, max 170 characters

  const titleText = 'alternatives and similar packages'
  const titleLong = `${params.repoOwner}/${params.repoName} ${titleText}`;
  const titleShort = `${params.repoName} ${titleText}`;
  const title = titleLong.length <= 60 ? titleLong : titleShort;

  // `data` could be undefined in case of not found repo. Was causing 500 errors instead of 404
  const top6Results = (data?.relatedResults || []).slice(0, 6).map(relatedResult => relatedResult.repo_name.split('/')[1]);
  let description = `Alternatives to ${params.repoName}: ${params.repoName} vs ${top6Results[0]}. ${top6Results[1]} vs ${top6Results[2]}`;
  const descriptionAdditional = `. ${top6Results[3]} vs ${top6Results[4]}`;
  if (description.length + descriptionAdditional.length <= 160) {
    description = description + descriptionAdditional;
  }

  const canonicalURL = `${__BASE_URL__}/gh/${params.repoOwner}/${params.repoName}`;

  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": canonicalURL,
    "relatedLink": `https://github.com/${params.repoOwner}/${params.repoName}`
  };

  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonicalURL },
    { "script:ld+json": jsonLD },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalURL },
    { property: "og:image", content: __OG_IMAGE__ }
  ];
};

const selectOptionsDict = {
  'Most Relevant': 'relevanceScore',
  'Most Stars': 'stargazerCount',
  'Recently Updated': 'pushedAtNumericValue'
}

const feedbackKey = 'rr-feedback';

function getFeedbackKeyRepoName(loaderData: any): string {
  return `${loaderData.repoOwner}/${loaderData.repoName}`.toLowerCase();
}

function getFeedbackObj() {
  return JSON.parse(localStorage.getItem(feedbackKey) ?? '{}');
}

function isMoreThanOneDayOld(timestamp: number): boolean {
  const oneDay = 24 * 60 * 60 * 1000; // One day in milliseconds
  const currentTime = Date.now();
  return (currentTime - timestamp) > oneDay;
}

function showExtLinksModal(e) {
  getModalElement('my_modal_1').showModal();
}

export async function loader({
  params,
}: LoaderFunctionArgs) {
  const faissResponse = await getRelatedRepos(`${params.repoOwner}/${params.repoName}`);
  return json({
    repoOwner: params.repoOwner,
    repoName: params.repoName,
    watchCount: faissResponse.watch_count,
    description: faissResponse.description,
    pushedAt: faissResponse.pushedAt,
    stargazerCount: faissResponse.stargazerCount,
    relatedResults: faissResponse.results
  },
  { headers: apiCacheControlHeader });
}

export default function Index() {
  const loaderData: any = useLoaderData();
  const starCount: number = loaderData.stargazerCount ?? loaderData.watchCount;

  const [feedbackRecieved, setFeedbackRecieved] = useState<boolean>(false);
  const [selectedSortOption, setSelectedSortOption] = useState('Most Relevant');
  const selectedSortKey = selectOptionsDict[selectedSortOption];

  // make a copy of the array to avoid sorting mutation
  const sortedResults: RepoResult[] = [...loaderData.relatedResults].sort((a,b) => {
    return b[selectedSortKey] - a[selectedSortKey];
  });

  const handleButtonGroupChange = (buttonOption: string) => {
    setSelectedSortOption(buttonOption);
    // TODO: persist state in URL query param? list reverts when clicking back button
  };


  useEffect(() => {
    // clean up old items on component mount
    const feedbackObj = getFeedbackObj();
    for(const repoFullName of Object.keys(feedbackObj)) {
      const timeStamp = feedbackObj[repoFullName].timeStamp;
      if (isMoreThanOneDayOld(timeStamp)) {
        delete feedbackObj[repoFullName];
      }
    }
    localStorage.setItem(feedbackKey, JSON.stringify(feedbackObj));
  }, []);

  useEffect(() => {
    const feedbackObj = getFeedbackObj();
    setFeedbackRecieved(feedbackObj.hasOwnProperty(getFeedbackKeyRepoName(loaderData)));
    addToHistory(HistoryType.RepoVisit, `${loaderData.repoOwner}/${loaderData.repoName}`)
  }, [loaderData]); // loaderData dependency in case of page navigation

  const setFeedbackLocalStorage = (repoFullName: string, positiveFeedback: boolean) => {
    const feedbackObj = getFeedbackObj();
    // set new data
    feedbackObj[repoFullName] = {
      timeStamp: Date.now(),
      positiveFeedback
    }
    localStorage.setItem(feedbackKey, JSON.stringify(feedbackObj));
    setFeedbackRecieved(true);
  };

  const handleFeedbackClick = async (positiveFeedback: boolean) => {    
    const url = `/api/feedback/gh/${loaderData.repoOwner}/${loaderData.repoName}/${positiveFeedback}`;
    const r = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        repoOwner: loaderData.repoOwner,
        repoName: loaderData.repoName,
        resultsFeedBackPositive: positiveFeedback
      })
    });

    setFeedbackLocalStorage(getFeedbackKeyRepoName(loaderData), positiveFeedback);
    showThankYouToast();
  };

  // const starHistEmbedURL = `https://api.star-history.com/svg?repos=${top5RepoNames.join(',')}`;
  // const startHistStyles = {
  //   width: '100%',
  //   height: 'auto',
  //   minWidth: '600px', // Note the camelCase for min-width
  //   minHeight: '600px' // Note the camelCase for min-height
  // };

  return (
    <>
      <section className="background-gray px-2 py-2 border border-gray-100 rounded-md">

      <dialog id="my_modal_1" className="modal">
        {/* https://daisyui.com/components/modal/?lang=en */}
        <div className="modal-box">
          <h3 className="text-lg font-bold py-2">{loaderData.repoOwner}/{loaderData.repoName} <span className="text-base font-normal text-rr-medium">external links</span></h3>
          <hr />

          <ExternalLinksList repoName={`${loaderData.repoOwner}/${loaderData.repoName}`} />

          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn">Close</button>
            </form>
          </div>
        </div>
      </dialog>

        <h2 className="flex items-center py-1">
          <span className="text-xl font-bold mr-1 ">
            {loaderData.repoOwner} / {loaderData.repoName}
          </span>
          <a onClick={showExtLinksModal} className="ml-2 cursor-pointer tooltip inline-flex items-center text-rr-light" data-tip="View external links" aria-label="View external links">
            <LinkIcon className="inline-block size-3 mr-1" />
            <span>Links</span>
          </a>
        </h2>
        {(loaderData.description !== 'None' && loaderData.description !== 'nan') && 
          <div className="text-sm text-rr-medium py-1">
            <Description description={loaderData.description} maxLength={347} />
          </div>
        }
        <div className="text-sm text-rr-light">
          <span className="mr-2">☆{starCount.toLocaleString()}</span>
          <UpdatedAt updatedAt={loaderData.pushedAt} />
        </div>
      </section>

      <h1 className="pt-3 text-lg font-semibold text-rr-medium">
        Alternatives and similar repositories for {loaderData.repoName}
      </h1>

      <p className="text-sm text-rr-light pl-1">
        Users that are interested in {loaderData.repoName} are comparing it to the libraries listed below
      </p>

      <div className='py-2 pl-1'>
        <div className='inline-block mr-1 text-sm text-rr-medium'>Sorting:</div>
        <div className='inline-block'>
          <ButtonGroup options={Object.keys(selectOptionsDict)} selectedOption={selectedSortOption} onSelect={handleButtonGroupChange} />
        </div>
      </div>

      <hr />

      {/* <iframe style={startHistStyles} src={starHistEmbedURL} frameBorder="0"></iframe> */}

      <ul role="list" className="divide-y divide-gray-100" >
        {sortedResults.map((repoResult: RepoResult, i: number) => (
          <RemixRepoListItem key={i} repoResult={repoResult} />
        ))}
      </ul>

      {feedbackRecieved ? null :
        <div id="feedback-button" className="inline-flex items-center gap-x-1.5 background-button-white rounded-md ring-1 ring-inset ring-gray-300 px-3 py-2 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 tooltip tooltip-left" data-tip="Are these results useful?">
          <span className="text-rr-button-white-primary hidden md:inline">Results:</span>
          <HandThumbUpIcon onClick={() => handleFeedbackClick(true)} className="h-5 w-5 text-rr-button-white-primary hover:cursor-pointer mr-1 md:mr-0" />
          <HandThumbDownIcon onClick={() => handleFeedbackClick(false)} className="h-5 w-5 text-rr-button-white-primary hover:cursor-pointer" />
        </div>
      }

    </>
  );
}

export function ErrorBoundary() {
  const error: any = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
      <div>
        <h1 className="text-xl font-bold mb-2">
          Repository not found
        </h1>

        <h2 className="flex items-center py-1">
          <span className="text-lg font-bold mr-1 ">
            {error.data}
          </span>
          <a href={`https://github.com/${error.data}`} target="_blank" className="ml-1 tooltip inline-flex items-center" data-tip="View on GitHub" aria-label={`View "${error.data}" on GitHub`}>
            <ArrowTopRightOnSquareIcon className="inline-block size-4 text-gray-400" />
          </a>
        </h2>

        <p>That repository was not found in the index. Currently only repositories with a minimum of 15 stars are indexed.</p>
        <p>Feel free to check back again in the future after the repo gains some popularity.</p>
      </div>
      )
    }
    else {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          {/* only show error message in development */}
          <p>{process.env.NODE_ENV === "development" ? (error?.data ?? "Unknown error") : null}</p>
          {/* <p>{error.data}</p> */}
        </div>
      );
    }
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        { (process.env.NODE_ENV === "development" && error.stack) ? <>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </> : null}
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}
